/**
 * useLyricPolisher — React hook for multi-agent lyric polishing.
 * Wraps runLyricPolish with state management, abort control, and mounted guard.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { runLyricPolish } from '@/services/lyricPolishService';
import type {
  PolishResult,
  PolishStatus,
  PolishProgressEvent,
  LyricDeliberationConfig,
  LyricArchitectureContext,
} from '@/types/lyricOrchestrator.types';

export function useLyricPolisher() {
  const [status, setStatus] = useState<PolishStatus>('idle');
  const [result, setResult] = useState<PolishResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<PolishProgressEvent | null>(null);
  const [completedAgents, setCompletedAgents] = useState<string[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const startPolish = useCallback(async (
    lyrics: string,
    genre: string,
    moods: string[],
    arch?: LyricArchitectureContext,
    config?: Partial<LyricDeliberationConfig>,
  ) => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    setStatus('reviewing');
    setResult(null);
    setError(null);
    setProgress(null);
    setCompletedAgents([]);

    try {
      const polishResult = await runLyricPolish(
        lyrics,
        genre,
        moods,
        arch,
        config,
        abortRef.current.signal,
        (event) => {
          if (!mountedRef.current) return;
          setProgress(event);
          if (event.type === 'reviewer-complete') {
            setCompletedAgents(prev => [...prev, event.agentId]);
          }
          if (event.type === 'polish-synthesis-start') {
            setStatus('polishing');
          }
        }
      );
      if (!mountedRef.current) return;
      setResult(polishResult);
      setStatus('complete');
    } catch (err: unknown) {
      if (!mountedRef.current) return;
      if (err instanceof DOMException && err.name === 'AbortError') {
        setStatus('aborted');
        return;
      }
      setError(err instanceof Error ? err.message : 'Lyric polish failed');
      setStatus('failed');
    }
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    if (mountedRef.current) {
      setStatus('aborted');
    }
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setResult(null);
    setError(null);
    setProgress(null);
    setCompletedAgents([]);
  }, []);

  return {
    status,
    isPolishing: status === 'reviewing' || status === 'polishing',
    result,
    error,
    progress,
    completedAgents,
    startPolish,
    cancel,
    reset,
  };
}
